import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../lib/api'
import { queryKeys } from '../lib/queryKeys'

/**
 * Fetch webhook event stats (counts by status).
 * Auto-refreshes every 30 seconds to keep the dashboard banner live.
 */
export function useWebhookEventStats() {
  return useQuery({
    queryKey: queryKeys.webhookEvents.stats(),
    queryFn: api.getWebhookEventStats,
    refetchInterval: 30_000,
  })
}

/**
 * Fetch paginated webhook events with optional status filter.
 */
export function useWebhookEvents(params?: Record<string, string>) {
  return useQuery({
    queryKey: queryKeys.webhookEvents.list(params),
    queryFn: () => api.getWebhookEvents(params),
    refetchInterval: 30_000,
  })
}

/**
 * Fetch the processing-attempt trail for one meeting (B-25).
 * Only mounted when a row is expanded, so no polling needed.
 */
export function useProcessingAttempts(meetingId: string) {
  return useQuery({
    queryKey: queryKeys.webhookEvents.attempts(meetingId),
    queryFn: () => api.getProcessingAttempts(meetingId),
  })
}

/**
 * Retry a single failed/dead webhook event.
 * Invalidates both the event list and stats on success.
 */
export function useRetryWebhookEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.retryWebhookEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.webhookEvents.all })
    },
  })
}

/**
 * Retry all failed webhook events at once.
 * Invalidates both the event list and stats on success.
 */
export function useRetryAllFailed() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.retryAllFailed,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.webhookEvents.all })
    },
  })
}
